import styles from './top-tracks.module.css'

import { LuGripVertical } from '@react-icons/all-files/lu/LuGripVertical'
import { LuPlus } from '@react-icons/all-files/lu/LuPlus'
import { LuTrash2 } from '@react-icons/all-files/lu/LuTrash2'
import isEqual from 'lodash/isEqual'
import React, { useEffect, useState } from 'react'

import { TRACK } from '@disco/constants/ItemTypes'
import { useTranslation } from '@disco/i18n/use-translation'
import {
  clientLibraryFeaturedTracksAlter,
  clientLibraryFeaturedTracksLoad,
  clientLibraryFeaturedTracksReorder,
} from '@disco/state-management/actions/client-library-track-actions'
import { getClientLibraryTracks } from '@disco/state-management/selectors/client-libraries'
import { pushUINotification } from '@disco/utils/notifications'

import type { ClientLibraryId } from 'types/client-library'
import type { Track, TrackId } from 'types/track'

import Waveform from 'components/common/player/waveform'
import TrackInfo from 'components/common/track-info'
import {
  Box,
  Button,
  Flex,
  IconButton,
  Spinner,
  Text,
  Tooltip,
} from 'components/ds'
import Draggable from 'components/ui/draggable'
import { HoverProvider } from 'components/ui/hover-provider'
import PageContainer from 'components/ui/page-container'
import Sortable from 'components/ui/sortable'

import { useDispatch, useSelector } from 'hooks/use-redux'

import AddTopTracksDialog from './add-top-tracks-dialog'
import { MAX_TOP_TRACKS } from './constants'

type Props = {
  libraryId: ClientLibraryId
}

/**
 * "Manage top tracks" section of the artist profile page. Top tracks are the
 * catalog's featured tracks, picked from anywhere in the business library.
 * @param libraryId - ID of the artist profile catalog
 */
const TopTracks = ({ libraryId }: Props) => {
  const { t } = useTranslation(['catalogs-management', 'common'])
  const dispatch = useDispatch()
  const featured = useSelector(
    state => getClientLibraryTracks(state, libraryId)?.featured,
  )
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  // The thunks undo a failed change by restoring the list from before it, so only one
  // change can be saving at a time
  const [isSaving, setIsSaving] = useState(false)
  const topTracks: Track[] = featured?.list ?? []
  const topTrackIds = topTracks.map(track => track.id)
  const remaining = Math.max(MAX_TOP_TRACKS - topTracks.length, 0)

  useEffect(() => {
    dispatch(clientLibraryFeaturedTracksLoad(libraryId, ''))
  }, [dispatch, libraryId])

  const loadTopTracks = () => {
    dispatch(clientLibraryFeaturedTracksLoad(libraryId, ''))
  }

  const save = async (change: Promise<unknown>, errorMessage: string) => {
    setIsSaving(true)
    try {
      await change
      pushUINotification({
        message: t('catalogs-management:topTracksUpdated', 'Tracks updated!'),
        level: 'success',
      })
    } catch {
      pushUINotification({ message: errorMessage, level: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  const removeTrack = (trackId: TrackId) => () => {
    save(
      dispatch(clientLibraryFeaturedTracksAlter(libraryId, [], [trackId])),
      t(
        'catalogs-management:topTracksCouldNotBeUpdated',
        'Tracks could not be updated, please try again.',
      ),
    )
  }

  const reorder = ({ order }: { order: string[] }) => {
    const trackIds = order.map(Number)

    if (!isEqual(topTrackIds, trackIds)) {
      save(
        dispatch(clientLibraryFeaturedTracksReorder(libraryId, trackIds)),
        t(
          'catalogs-management:topTracksCouldNotBeReordered',
          'Tracks could not be reordered, please try again.',
        ),
      )
    }
  }

  const addButton = (
    <Button
      disabled={remaining === 0 || isSaving}
      onClick={() => setIsDialogOpen(true)}
    >
      <LuPlus />
      {t('catalogs-management:addTrack', 'Add track')}
    </Button>
  )

  const renderTrack = (track: Track | undefined, index: number) => {
    // Sortable renders its previous order once before syncing to a removed track
    if (!track) {
      return null
    }

    return (
      <Draggable key={track.id} id={track.id} type={TRACK} disabled={isSaving}>
        <HoverProvider>
          <Flex align="center" gap="3" py="2" className={styles.trackRow}>
            <LuGripVertical className={styles.dragHandle} aria-hidden />
            <Text size="2" color="gray" className={styles.index}>
              {(index + 1).toString().padStart(2, '0')}
            </Text>
            <Box flexGrow="1" flexBasis="0" minWidth="0">
              <TrackInfo location="explorer" track={track} />
            </Box>
            <Waveform
              className={styles.waveform}
              track={track}
              barWidth={1.12}
              barGap={2.18 - 1.12}
              noAnimation
            />
            <Tooltip
              content={t(
                'catalogs-management:removeFromTopTracks',
                'Remove from top tracks',
              )}
            >
              <IconButton
                variant="ghost"
                color="gray"
                aria-label={t(
                  'catalogs-management:removeTrackName',
                  'Remove {{name}}',
                  {
                    name: track.name,
                  },
                )}
                disabled={isSaving}
                onClick={removeTrack(track.id)}
              >
                <LuTrash2 />
              </IconButton>
            </Tooltip>
          </Flex>
        </HoverProvider>
      </Draggable>
    )
  }

  const renderContent = () => {
    if (!featured?.loaded && featured?.error) {
      return (
        <Flex direction="column" align="center" gap="3" p="4">
          <Text size="2" color="gray">
            {t(
              'catalogs-management:couldNotLoadTopTracks',
              'Couldn’t load top tracks.',
            )}
          </Text>
          <Button variant="soft" onClick={loadTopTracks}>
            {t('common:tryAgain', 'Try again')}
          </Button>
        </Flex>
      )
    }
    // Also covers the first render, before the load has been dispatched
    if (!featured?.loaded) {
      return (
        <Flex justify="center" p="4">
          <Spinner />
        </Flex>
      )
    }
    if (topTracks.length === 0) {
      return (
        <button
          type="button"
          className={styles.emptyState}
          disabled={isSaving}
          onClick={() => setIsDialogOpen(true)}
        >
          <Flex align="center" justify="center" gap="2">
            <LuPlus />
            <Text size="2">
              {t(
                'catalogs-management:addYourFirstTopTrack',
                'Add your first top track',
              )}
            </Text>
          </Flex>
        </button>
      )
    }

    return (
      <Sortable
        accept={[TRACK]}
        id={`${libraryId}-top-tracks`}
        items={topTracks}
        keyExtractor={(track: Track) => String(track.id)}
        renderItem={renderTrack}
        onDrop={reorder}
      />
    )
  }

  return (
    <PageContainer.Section
      title={t('catalogs-management:manageTopTracks', 'Manage top tracks')}
      action={
        featured?.loaded && (
          <Flex align="center" gap="3">
            <Text size="2" color="gray">
              {t('catalogs-management:topTracksCount', '{{current}}/{{max}}', {
                current: topTracks.length,
                max: MAX_TOP_TRACKS,
              })}
            </Text>
            {remaining === 0 ? (
              <Tooltip
                content={t(
                  'catalogs-management:topTracksLimitReached',
                  'You can add up to {{max}} top tracks',
                  {
                    max: MAX_TOP_TRACKS,
                  },
                )}
              >
                <span>{addButton}</span>
              </Tooltip>
            ) : (
              addButton
            )}
          </Flex>
        )
      }
    >
      {renderContent()}
      <AddTopTracksDialog
        libraryId={libraryId}
        excludeIds={topTrackIds}
        remaining={remaining}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />
    </PageContainer.Section>
  )
}

export default TopTracks
